import {
    AfterViewChecked,
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import { MatSort, SortDirection } from '@angular/material/sort';
import { Navigate } from '@ngxs/router-plugin';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import { FetchAllExecutions, FetchAllExecutionsFailure, FetchAllExecutionsSuccess, SortAllExecutions } from 'app/library/all-modules/actions';
import { IChoosenFilterEntity } from 'app/library/all-users/interfaces/choosen-filter.interface';
import { ExtendedAttributeFilterInterface } from 'app/library/all-users/interfaces/extended-attribute-filer.interface';
import { ExtendedAttributeCategoryEnum } from 'app/library/extended-attributes/enums/extended-attribute-category.enum';
import { ExtendedAttribute } from 'app/library/extended-attributes/models/extended-attribute';
import { ExtendedAttributeService } from 'app/library/extended-attributes/services/extended-attribute.service';
import { ModuleKind } from 'app/library/module/enums/module-kind.enum';
import { ModuleStatuses } from 'app/library/module/models/module-statuses';
import { Module } from 'app/library/module/models/module.model';
import { ModuleGlobalService } from 'app/library/module/services/module-global.service';
import { MaxBrainTag } from 'app/library/tag/models/tag.model';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { FeatureSwitchPipe } from 'app/projects/shared/src/lib/pipes/feature-switch.pipe';
import { PageSizePreferenceService } from 'app/projects/shared/src/lib/services/page-size-preference.service';
import { User } from 'app/projects/user/src/lib/models/user';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { debounceTime, filter, map, skip, takeUntil } from 'rxjs/operators';
import { ModuleManagersService } from '../../../module-managers/services/module-managers.service';
import { AllModulesState } from '../../models/all-modules.state';
import { AllExecutionsDataSource } from './datasource';

@Component({
    selector: 'app-executions-list',
    templateUrl: './executions-list.component.html',
    styleUrls: ['./executions-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class AllExecutionsListComponent extends UnsubscribeOnDestroy implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked {
    private readonly _fixedColumns = new Set(['avatar', 'buttons']);
    private readonly _defaultHiddenColumnIds = new Set(['createdAt', 'updatedAt']);
    private _getValueFromSessionStorage<T>(storageKey: string, fallback: T): T {
        const rawValue = sessionStorage.getItem(storageKey);

        if (!rawValue || rawValue === 'undefined') {
            return fallback;
        }

        try {
            const parsedValue = JSON.parse(rawValue) as T;
            return parsedValue ?? fallback;
        } catch {
            return fallback;
        }
    }

    private _getEntityIdsFromSessionStorage(storageKey: string): string[] {
        const storedValues = this._getValueFromSessionStorage<any[]>(storageKey, []);

        if (!storedValues?.length) {
            return [];
        }

        return storedValues.map((item) => (typeof item === 'string' ? item : item?.id)).filter((id) => !!id);
    }

    @Input()
    sortBy: string;

    @Input()
    sortOrder: SortDirection;

    @Input()
    loadModule$: Observable<boolean>;

    @Input()
    filteredModule$: Observable<Module[]>;

    @Input()
    module$: Observable<Module[]>;

    @Input()
    searchTerm$: Observable<string>;
    searchTerm: string;

    @Input()
    totalCount$: Observable<number>;

    @Input()
    tag$: Observable<IChoosenFilterEntity[]> = of([]);
    tagIds: string[] = [];

    @Input()
    statuses$: Observable<IChoosenFilterEntity[]> = of([]);
    statusIds: string[];

    @Input()
    types$: Observable<IChoosenFilterEntity[]> = of([]);
    typeIds: string[] = [];

    @Input()
    profile: User;

    @Input()
    possibleTableColumns: {
        id: string;
        translation: string;
    }[] = [];
    displayedColumns: string[] = [];

    @Input()
    extendedAttributes$: Observable<ExtendedAttributeFilterInterface[]> = of([]);
    extendedAttributesFilters: ExtendedAttributeFilterInterface[] = [];
    extendedAttributes: ExtendedAttribute[] = [];

    @Input()
    executionDate$: Observable<ExtendedAttributeFilterInterface> = of(null);
    executionDateFilter: ExtendedAttributeFilterInterface;

    @ViewChild(MatPaginator)
    paginator: MatPaginator;
    @ViewChild(MatSort)
    sort: MatSort;
    @ViewChild('chipList')
    chipList: ElementRef;
    @ViewChild('columnsSelect')
    columnsSelect: MatSelect;

    @ViewChild('ruler', { static: true })
    ruler: ElementRef<HTMLSpanElement>;

    @Output()
    clickedTag = new EventEmitter<MaxBrainTag>();

    @Output()
    updateList = new EventEmitter<Module[]>();

    dataSource: AllExecutionsDataSource;
    confirmDialogRef: MatDialogRef<MaxBrainConfirmDialogComponent>;
    initialLoad: boolean = true;
    list: Module[] = [];
    tableColumnsDropdown = new FormControl(null);
    private _columnsSnapshot: string[] | null = null;
    private _applyClicked = false;
    isLoading: boolean = true;
    /**
     * Bumps after the table becomes visible / resizes so filterTags remeasures with a real cell width.
     * (Pure pipe would otherwise keep a stale empty/partial result from the hidden table.)
     */
    tagsLayoutKey = 0;
    private readonly _resize$ = new Subject<void>();
    isExtendedAttributesFeatureEnabled: boolean = false;
    moduleStatuses = ModuleStatuses;

    @HostListener('window:resize')
    onWindowResize(): void {
        this._resize$.next();
    }

    private _saveColumnsToSessionStorage(): void {
        sessionStorage.setItem('allExecutionsDisplayedColumns', JSON.stringify(this.displayedColumns));
    }

    private _normalizeDisplayedColumns(columns: string[] | null | undefined): string[] {
        const selected = new Set([...(columns || []), ...this._fixedColumns]);
        return this.possibleTableColumns.map((c) => c.id).filter((id) => selected.has(id));
    }

    private readonly _pageSizePreferenceKey = 'all-modules.executions-list';
    private readonly _pageSizeOptions = [10, 25, 50];

    constructor(
        public matDialog: MatDialog,
        private _store: Store,
        private _action$: Actions,
        private _moduleGlobalService: ModuleGlobalService,
        private _moduleManagersService: ModuleManagersService,
        private _cd: ChangeDetectorRef,
        private _featureSwitchPipe: FeatureSwitchPipe,
        private _extendedAttributeService: ExtendedAttributeService,
        private _pageSizePreferenceService: PageSizePreferenceService
    ) {
        super();

        this.isExtendedAttributesFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.ExtendedAttributes);
        this._resize$.pipe(debounceTime(150), takeUntil(this._unsubscribeAll)).subscribe(() => this._scheduleTagsRemeasure());
    }

    async ngOnInit(): Promise<void> {
        const defaultColumnIds = this.possibleTableColumns.map((c) => c.id);

        if (this.isExtendedAttributesFeatureEnabled) {
            await this._extendedAttributeService.fetchExtendedAttributes([ExtendedAttributeCategoryEnum.COURSE_OFFERING, ExtendedAttributeCategoryEnum.COURSE_EXECUTION]);

            this.extendedAttributes = this._extendedAttributeService.getExtendedAttributes();

            this.extendedAttributes.forEach((attribute) => {
                const tempAttribute = {
                    id: attribute.name,
                    translation: attribute.translationKey,
                };

                this.possibleTableColumns.splice(this.possibleTableColumns.length - 1, 0, tempAttribute);
            });
        }

        this.setTableColumns(defaultColumnIds);

        this.loadModule$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((resetPageIndex) => resetPageIndex !== null)
            )
            .subscribe((resetPageIndex) => {
                this.loadModulesPage(resetPageIndex);
            });

        const preferredPageSize = this._pageSizePreferenceService.getPageSize(this._pageSizePreferenceKey, this._pageSizeOptions, 10);

        this._store.dispatch(
            new FetchAllExecutions({
                search: '',
                pageSize: preferredPageSize,
                pageNumber: 1,
                sortBy: '',
                sortOrder: '',
                statusesIds: this._getEntityIdsFromSessionStorage('allExecutionsStatusesFilter'),
                tags: this._getEntityIdsFromSessionStorage('allExecutionsTagsFilter'),
                types: this._getEntityIdsFromSessionStorage('allExecutionsTypesFilter'),
                extendedAttributesFilters: this._getValueFromSessionStorage<ExtendedAttributeFilterInterface[]>('allExecutionsAttributesFilter', []),
                executionDate: this._getValueFromSessionStorage<ExtendedAttributeFilterInterface>('allExecutionsDateFilter', null),
                columns: [],
            })
        );

        // TODO: Fix _loadingSubject in datasource and remove this subscription
        this._action$.pipe(ofActionDispatched(FetchAllExecutionsSuccess, FetchAllExecutionsFailure), takeUntil(this._unsubscribeAll)).subscribe(() => {
            if (this.initialLoad) {
                setTimeout(() => {
                    const lastPageSize = this._store.selectSnapshot(AllModulesState.getPageSize);

                    if (lastPageSize) {
                        this.paginator.pageSize = lastPageSize;
                    }

                    this.paginator.pageIndex = this._store.selectSnapshot(AllModulesState.getPageIndex);

                    this.paginator.page.subscribe(({ length, pageIndex, pageSize }: PageEvent) => {
                        const itemsleft = length - pageIndex * pageSize;
                        const itemsInThisPage = itemsleft < pageSize ? itemsleft : pageSize;
                        // FIXME: these hardcoded numbers represent the height of the rest of the elements surrounding the table within the layout content
                        const tableHeight = 80 + 24 + 64 + 56 + itemsInThisPage * 65 + 72 + 56;
                        const contentElementHeight = document.getElementById('container-2').offsetHeight;
                        const scrollTop = tableHeight > contentElementHeight ? tableHeight - contentElementHeight : 0;

                        setTimeout(() => {
                            document.getElementById('container-3').scrollTop = scrollTop;
                        });
                    });

                    this.paginator.page.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
                        this.loadModulesPage(false);
                    });

                    this.sort.sortChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((sort) => {
                        this._store.dispatch(new SortAllExecutions(sort));

                        this.loadModulesPage(true);
                    });

                    const preferredPageSize = this._pageSizePreferenceService.getPageSize(this._pageSizePreferenceKey, this._pageSizeOptions, 10);
                    if (this.paginator.pageSize !== preferredPageSize) {
                        this.paginator._changePageSize(preferredPageSize);
                    }
                });
            }

            // Initial fetch bypasses dataSource.loadModules, so loading$ never flips.
            // Remeasure only after the table wrapper is unhidden (initialLoad=false).
            this.initialLoad = false;
            this.isLoading = false;
            this._scheduleTagsRemeasure();
        });
    }

    ngAfterViewInit(): void {
        this.dataSource = new AllExecutionsDataSource(
            this.filteredModule$.pipe(
                map((modules) =>
                    modules
                        .filter((module) => module.kind !== ModuleKind.Normal)
                        .map((item) => {
                            item = Object.assign(new Module(), item, {
                                allAttributes: [...item.attributes, ...item.courseOffering.attributes],
                            });
                            return item;
                        })
                )
            ),
            this._store,
            this._action$
        );
        this._cd.detectChanges();

        // skip(1): BehaviorSubject starts as false; that emit happens while the table may
        // still be display:none (initialLoad). Measuring then sticks filterTags on "...".
        this.dataSource.loading$.pipe(skip(1), takeUntil(this._unsubscribeAll)).subscribe((isLoading) => {
            this.isLoading = isLoading;
            if (!isLoading) {
                this._scheduleTagsRemeasure();
            }
        });

        combineLatest([this.statuses$, this.tag$, this.types$, this.extendedAttributes$, this.executionDate$])
            .pipe(
                filter(([selectedStatuses, selectedTags, selectedTypes, extendedAttributes, executionDate], index) => {
                    this.statusIds = [];
                    if (selectedStatuses?.length) {
                        selectedStatuses.forEach((selectedStatus) => {
                            this.statusIds.push(selectedStatus.id);
                        });
                    }

                    this.tagIds = [];
                    if (selectedTags?.length) {
                        selectedTags.forEach((selectedTag) => {
                            this.tagIds.push(selectedTag.id);
                        });
                    }

                    this.typeIds = [];
                    if (selectedTypes?.length) {
                        selectedTypes.forEach((selectedType) => {
                            this.typeIds.push(selectedType.id);
                        });
                    }

                    this.extendedAttributesFilters = [];
                    if (extendedAttributes?.length) {
                        this.extendedAttributesFilters = extendedAttributes;
                    }

                    this.executionDateFilter = executionDate || null;

                    return index > 0;
                }),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((value) => {
                setTimeout(() => {
                    this.loadModulesPage(true);
                }, 200);
            });

        this.searchTerm$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((searchTerm, index) => {
                    this.searchTerm = searchTerm || '';
                    return index > 0;
                })
            )
            .subscribe(() => {
                this.loadModulesPage(true);
            });

    }

    ngAfterViewChecked(): void {
        this._cd.detectChanges();
    }

    setTableColumns(defaultColumnIds?: string[]): void {
        const stored = this._getValueFromSessionStorage<string[]>('allExecutionsDisplayedColumns', null);
        const fallback = (defaultColumnIds || this.possibleTableColumns.map((column) => column.id)).filter((columnId) => !this._defaultHiddenColumnIds.has(columnId));
        this.displayedColumns = this._normalizeDisplayedColumns(stored || fallback);

        this.tableColumnsDropdown.setValue(this.displayedColumns);
    }

    onColumnsDropdownOpenedChange(opened: boolean): void {
        if (opened) {
            this._columnsSnapshot = [...this.displayedColumns];
        } else {
            if (this._applyClicked) {
                this._applyClicked = false;
            } else if (this._columnsSnapshot) {
                this.tableColumnsDropdown.setValue([...this._columnsSnapshot], { emitEvent: false });
            }
            this._columnsSnapshot = null;
        }
    }

    applyColumns(): void {
        this._applyClicked = true;
        const currentValue = this.tableColumnsDropdown.value;
        if (currentValue) {
            this.displayedColumns = this._normalizeDisplayedColumns([...currentValue]);
            this.loadModulesPage();
            this._saveColumnsToSessionStorage();
            this._scheduleTagsRemeasure();
        }
        this.columnsSelect.close();
    }

    /**
     * Pure filterTags caches by layout key. Bump only after the table is visible and laid out
     * (display:none → offsetWidth 0 would permanently show only "...").
     */
    private _scheduleTagsRemeasure(): void {
        setTimeout(() => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    this.tagsLayoutKey++;
                    this._cd.detectChanges();
                });
            });
        });
    }

    visualLength(text: string): number {
        const ruler = this.ruler?.nativeElement;
        if (!ruler) {
            return 0;
        }
        ruler.textContent = text;
        const width = ruler.offsetWidth;
        ruler.textContent = '';
        return width;
    }

    goToModule(module: Module): void {
        const normalizedStatus = (module?.status || '').toLowerCase();
        const courseOfferingId = module?.courseOffering?.id;

        if (normalizedStatus === this.moduleStatuses.PLANNED && courseOfferingId) {
            this._store.dispatch(new Navigate([`/admin/offering-details/${courseOfferingId}/catalog/execution/${module.id}`]));
            return;
        }

        this._moduleGlobalService.setParentUrl(window.location.pathname);
        this._moduleManagersService.goToModule(this.profile.id, module, 'overview');
    }

    filterByTag(tag: MaxBrainTag): void {
        this.clickedTag.emit(tag);
    }

    loadModulesPage(resetPageIndex = false): void {
        if (resetPageIndex) {
            this.paginator.pageIndex = 0;
        }

        this.dataSource.loadModules(
            this.searchTerm,
            this.paginator.pageSize,
            this.paginator.pageIndex + 1,
            this.sort.active,
            this.sort.direction,
            this.statusIds,
            this.tagIds,
            this.typeIds,
            this.extendedAttributesFilters,
            this.executionDateFilter,
            this.displayedColumns
        );
    }
}
